import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const FullscreenForm = ({ 
  steps, 
  onSubmit, 
  title = "Application",
  submitButtonText = "Submit",
  successTitle = "Thank You!",
  successMessage = "We've received your application and will be in touch soon."
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [direction, setDirection] = useState('next');
  const [formData, setFormData] = useState({});

  // Initialize form data from steps
  useEffect(() => {
    const initialData = {};
    steps.forEach(step => {
      step.fields.forEach(field => {
        initialData[field.name] = '';
      });
    });
    setFormData(initialData);
  }, [steps]);

  // Lock body scroll when form is active
  useEffect(() => {
    if (isFormActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFormActive]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep = () => {
    const currentFields = steps[currentStep].fields;
    for (const field of currentFields) {
      if (field.required && !formData[field.name]?.trim()) {
        toast.error(`Please fill in ${field.label}`);
        return false;
      }
    }
    if (currentStep === 0 && formData.email && !formData.email.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setDirection('next');
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const prevStep = () => {
    setDirection('prev');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setIsSubmitted(true);
      toast.success("Application submitted!");
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startForm = () => {
    setIsFormActive(true);
  };

  const closeForm = () => {
    setIsFormActive(false);
    setCurrentStep(0);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const CurrentIcon = steps[currentStep]?.icon;

  // Success Screen
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
            <Check className="w-10 h-10 text-white/80" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-4">
            {successTitle}
          </h2>
          <p className="text-white/60 text-lg mb-10">
            {successMessage}
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setIsFormActive(false);
              setCurrentStep(0);
              setFormData({});
            }}
            className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white hover:bg-white hover:text-black transition-all duration-500"
          >
            Done
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Start Button (not in form mode)
  if (!isFormActive) {
    return (
      <button
        onClick={startForm}
        className="group flex items-center justify-center gap-3 w-full px-8 py-5 bg-white text-black font-medium hover:bg-white/90 transition-all duration-300"
      >
        Start Application
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>
    );
  }

  // Fullscreen Form
  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <span className="text-white/50 text-sm">{title}</span>
        <button
          onClick={closeForm}
          className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-6 py-4">
        <div className="flex justify-between mb-2">
          <span className="text-white/40 text-xs">Step {currentStep + 1} of {steps.length}</span>
          <span className="text-white/40 text-xs">{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white/70 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className={`transition-all duration-400 ${
          direction === 'next' ? 'animate-slideInRight' : 'animate-slideInLeft'
        }`}>
          {/* Step Header */}
          <div className="flex items-center gap-4 mb-8">
            {CurrentIcon && (
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <CurrentIcon className="w-6 h-6 text-white/60" />
              </div>
            )}
            <div>
              <h3 className="font-serif text-2xl text-white">{steps[currentStep].title}</h3>
              <p className="text-white/50 text-sm">{steps[currentStep].subtitle}</p>
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-6">
            {steps[currentStep].fields.map((field) => (
              <div key={field.name}>
                <label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3 block">
                  {field.label}
                  {field.required && <span className="text-white/30 ml-1">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    rows={4}
                    className="w-full bg-white/[0.03] border border-white/10 px-5 py-4 text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none transition-all duration-300 resize-none text-base"
                  />
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    className="w-full bg-white/[0.03] border border-white/10 px-5 py-4 text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none transition-all duration-300 text-base"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="px-6 py-4 border-t border-white/5 flex justify-between items-center">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className={`flex items-center gap-2 px-4 py-3 text-white/50 hover:text-white transition-all ${
            currentStep === 0 ? 'opacity-0 pointer-events-none' : ''
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        
        <button
          onClick={nextStep}
          disabled={isSubmitting}
          className="flex items-center gap-3 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-all duration-300 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Submitting...
            </>
          ) : currentStep === steps.length - 1 ? (
            <>
              {submitButtonText}
              <Check className="w-5 h-5" />
            </>
          ) : (
            <>
              Next
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FullscreenForm;
